import { type ReactNode, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Eye, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { useCustomers } from '@/features/admin-customers/api';

import { useAuditLog } from '../api';
import { AUDIT_ACTION_VALUES, type AuditAction, type AuditLogEntry } from '../types';

const DEFAULT_PAGE_SIZE = 50;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props {
  // Pre-populates the customer filter (and hides the customer select when set).
  // Used by the customer detail page's Recent Activity tab.
  defaultCustomerIdFilter?: string;
  // Hide the whole filter row — used when this table is embedded in a context
  // that already constrains scope (the customer detail tab).
  showFilters?: boolean;
  limit?: number;
}

export function AuditLogTable({
  defaultCustomerIdFilter,
  showFilters = true,
  limit = DEFAULT_PAGE_SIZE,
}: Props) {
  const [customerId, setCustomerId] = useState<string>(defaultCustomerIdFilter ?? '');
  const [action, setAction] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      customerId: customerId || undefined,
      action: action ? (action as AuditAction) : undefined,
      // <input type="date"> emits YYYY-MM-DD; pad to a full ISO datetime so the
      // backend's date-time format accepts it.
      dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00.000Z`).toISOString() : undefined,
      dateTo: dateTo ? new Date(`${dateTo}T23:59:59.000Z`).toISOString() : undefined,
      limit,
      offset,
    }),
    [customerId, action, dateFrom, dateTo, limit, offset],
  );

  const audit = useAuditLog(filters);
  // Customer dropdown is admin-only data and only needed when the filter is
  // user-controlled — skip the request when a default is pinned.
  const customers = useCustomers('', { enabled: showFilters && !defaultCustomerIdFilter });
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (customers.data) {
      for (const c of customers.data.customers) {
        map.set(c.id, c.name || c.companySlug);
      }
    }
    return map;
  }, [customers.data]);

  const total = audit.data?.total ?? 0;

  const clearFilters = () => {
    if (!defaultCustomerIdFilter) setCustomerId('');
    setAction('');
    setDateFrom('');
    setDateTo('');
    setOffset(0);
  };

  const columns: DataTableColumn<AuditLogEntry>[] = [
    {
      id: 'when',
      header: 'When',
      cell: (entry) => {
        const createdAt = new Date(entry.createdAt);
        return (
          <span className="text-sm text-muted-foreground" title={createdAt.toISOString()}>
            {formatDistanceToNow(createdAt, { addSuffix: true })}
          </span>
        );
      },
    },
    {
      id: 'actor',
      header: 'Actor',
      cell: (entry) => <span className="text-sm">{actorText(entry, customerNameById)}</span>,
    },
    {
      id: 'action',
      header: 'Action',
      cell: (entry) => (
        <Badge variant="secondary" className="font-mono text-xs">
          {entry.action}
        </Badge>
      ),
    },
    {
      id: 'resource',
      header: 'Resource',
      cell: (entry) => (
        <span className="font-mono text-xs">
          {entry.resourceId
            ? `${entry.resourceType} (${abbreviateUuid(entry.resourceId)})`
            : entry.resourceType}
        </span>
      ),
    },
    {
      id: 'ip',
      header: 'IP',
      cell: (entry) => <span className="font-mono text-xs">{entry.ipAddress}</span>,
    },
    {
      id: 'detail',
      header: 'Detail',
      className: 'w-16',
      cell: (entry) => (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`View metadata for ${entry.id}`}
              data-testid={`metadata-trigger-${entry.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-96 max-w-[80vw]"
            data-testid={`metadata-popover-${entry.id}`}
          >
            <pre className="overflow-auto whitespace-pre-wrap break-all text-xs">
              {JSON.stringify(entry.metadata, null, 2)}
            </pre>
            <div className="mt-2 text-[10px] text-muted-foreground">
              user-agent: <span className="font-mono">{entry.userAgent}</span>
            </div>
          </PopoverContent>
        </Popover>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex flex-wrap items-end gap-3">
          {!defaultCustomerIdFilter && (
            <FilterField label="Customer">
              <Select
                value={customerId || 'all'}
                onValueChange={(v) => {
                  setCustomerId(v === 'all' ? '' : v);
                  setOffset(0);
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.data?.customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || c.companySlug}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          <FilterField label="Action">
            <Select
              value={action || 'all'}
              onValueChange={(v) => {
                setAction(v === 'all' ? '' : v);
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {AUDIT_ACTION_VALUES.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="From">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setOffset(0);
              }}
              className="w-44"
            />
          </FilterField>
          <FilterField label="To">
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setOffset(0);
              }}
              className="w-44"
            />
          </FilterField>
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={audit.data?.items}
        getRowKey={(entry) => entry.id}
        isLoading={audit.isLoading}
        isError={audit.isError}
        errorState={
          <div className="text-center">
            <div className="text-sm text-destructive">Couldn&apos;t load audit log.</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => audit.refetch()}>
              Retry
            </Button>
          </div>
        }
        emptyState={<EmptyState Icon={FileText} title="No audit entries match these filters." />}
        rowProps={(entry) => ({ 'data-testid': `audit-row-${entry.id}` })}
        skeletonRows={5}
      />

      {audit.isSuccess && total > limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function abbreviateUuid(id: string): string {
  if (UUID_RE.test(id)) return `${id.slice(0, 8)}…`;
  return id;
}

function actorText(entry: AuditLogEntry, customerNameById: Map<string, string>): string {
  if (entry.customerId) {
    return customerNameById.get(entry.customerId) ?? entry.customerId.slice(0, 8);
  }
  if (entry.adminId) {
    return `Admin: ${entry.adminId.slice(0, 12)}`;
  }
  return 'Unknown';
}
